import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import useSEO from "@/hooks/useSEO";
import { BreadcrumbSchema } from "@/components/SEOStructuredData";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import SkipToContent from "@/components/SkipToContent";
import ScrollReveal from "@/components/ScrollReveal";
import StaggerContainer from "@/components/StaggerContainer";
import TalentCTA from "@/components/TalentCTA";
import { Mic, Search, CalendarCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeHtml } from "@/lib/sanitize";

interface PageContent {
  section_key: string;
  content_en: string;
  content_fr: string;
  is_hidden: boolean;
}

const About = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.substring(0, 2) || "en";

  useSEO({
    title: t("about.title"),
    description: t("seo.aboutDescription", "Learn about First Stage Talent, a Canadian voiceover and acting agency with 29+ years of experience connecting producers with professional performers."),
  });

  // Fetch page content from database
  const { data: pageContent } = useQuery({
    queryKey: ["about-page-content"],
    queryFn: async () => {
      const { data } = await supabase
        .from("page_content")
        .select("section_key, content_en, content_fr, is_hidden")
        .eq("page_slug", "about");
      return data as PageContent[] | null;
    },
  });

  // Fetch Shelley Cohen's image from database
  const { data: shelleyData } = useQuery({
    queryKey: ["shelley-cohen"],
    queryFn: async () => {
      const { data } = await supabase
        .from("talent")
        .select("image_url")
        .eq("slug", "shelley-cohen")
        .maybeSingle();
      return data;
    },
  });

  // Helper to get content by section key
  const getContent = (key: string, fallback: string = "") => {
    const item = pageContent?.find(p => p.section_key === key);
    if (!item) return fallback;
    return currentLang === "fr" ? item.content_fr : item.content_en;
  };

  // Helper to check if a section group is hidden
  const isSectionHidden = (sectionKey: string) => {
    const item = pageContent?.find(p => p.section_key === sectionKey);
    return item?.is_hidden ?? false;
  };

  // Helper to strip HTML tags for plain text contexts
  const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  const breadcrumbs = [
    { name: currentLang === "fr" ? "Accueil" : "Home", url: `https://firststagetalent.com/${currentLang}` },
    { name: stripHtml(getContent("hero_title", t("about.title"))), url: `https://firststagetalent.com/${currentLang}/about` },
  ];

  const whatWeDo = [
    {
      icon: Mic,
      title: getContent("whatwedo_talent_title", t("about.talentRepresentation")),
      description: getContent("whatwedo_talent_desc", t("about.talentRepresentationDesc")),
    },
    {
      icon: Search,
      title: getContent("whatwedo_casting_title", t("about.castingSupport")),
      description: getContent("whatwedo_casting_desc", t("about.castingSupportDesc")),
    },
    {
      icon: CalendarCheck,
      title: getContent("whatwedo_session_title", t("about.sessionCoordination")),
      description: getContent("whatwedo_session_desc", t("about.sessionCoordinationDesc")),
    },
  ];

  const whyChooseUs = [
    { 
      title: getContent("whyproducers_fast_title", t("about.fastTurnaround")), 
      desc: getContent("whyproducers_fast_desc", t("about.fastTurnaroundDesc")) 
    },
    { 
      title: getContent("whyproducers_bilingual_title", t("about.curatedTalent")), 
      desc: getContent("whyproducers_bilingual_desc", t("about.curatedTalentDesc")) 
    },
    { 
      title: getContent("whyproducers_pricing_title", t("about.clearPricing")), 
      desc: getContent("whyproducers_pricing_desc", t("about.clearPricingDesc")) 
    },
    { 
      title: getContent("whyproducers_local_title", t("about.canadianFocus")), 
      desc: getContent("whyproducers_local_desc", t("about.canadianFocusDesc")) 
    },
  ];

  return (
    <div className="min-h-screen">
      <BreadcrumbSchema items={breadcrumbs} />
      <SkipToContent />
      <Header />
      <main id="main-content" tabIndex={-1} className="outline-none">
        <PageHero 
          title={stripHtml(getContent("hero_title", t("about.title")))} 
          subtitle={stripHtml(getContent("hero_subtitle", t("about.subtitle")))}
        />
        
        {/* Intro Section */}
        <section className="section-padding">
          <div className="container-wide">
            <ScrollReveal className="max-w-3xl">
              <div 
                className="text-editorial text-lg md:text-xl leading-relaxed prose prose-p:my-4"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(getContent("intro", `<p>${t("about.intro")}</p>`)) }}
              />
            </ScrollReveal>
          </div>
        </section>

        {/* History Section */}
        {!isSectionHidden("history_title") && (
          <section className="section-padding bg-secondary">
            <div className="container-wide">
              <ScrollReveal>
                <h2 className="heading-section mb-8 text-primary">
                  {stripHtml(getContent("history_title", t("about.history")))}
                </h2>
              </ScrollReveal>
              <ScrollReveal className="max-w-3xl">
                <div 
                  className="text-editorial space-y-6 prose prose-p:my-4"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(getContent("history_text", `<p>${t("about.historyPara1")}</p><p>${t("about.historyPara2")}</p><p>${t("about.historyPara3")}</p>`)) }}
                />
              </ScrollReveal>
            </div>
          </section>
        )}

        {/* What We Do */}
        {!isSectionHidden("whatwedo_title") && (
          <section className="section-padding">
            <div className="container-wide">
              <ScrollReveal>
                <h2 className="heading-section mb-12 text-primary">
                  {stripHtml(getContent("whatwedo_title", t("about.whatWeDo")))}
                </h2>
              </ScrollReveal>
              <StaggerContainer className="grid md:grid-cols-3 gap-8" staggerDelay={0.12}>
                {whatWeDo.map((item) => (
                  <div key={item.title} className="space-y-4">
                    <div className="w-12 h-12 bg-primary text-primary-foreground flex items-center justify-center">
                      <item.icon size={24} />
                    </div>
                    <h3 className="heading-card text-primary">{stripHtml(item.title)}</h3>
                    <div 
                      className="text-editorial prose prose-p:my-2"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.description) }}
                    />
                  </div>
                ))}
              </StaggerContainer>
            </div>
          </section>
        )}

        {/* Our Approach */}
        {!isSectionHidden("approach_title") && (
          <section className="section-padding bg-secondary">
            <div className="container-wide">
              <div className="grid md:grid-cols-2 gap-16 items-center">
                <ScrollReveal animation="slideLeft">
                  <h2 className="heading-section mb-8 text-primary">
                    {stripHtml(getContent("approach_title", t("about.ourApproach")))}
                  </h2>
                  <div 
                    className="text-editorial space-y-6 prose prose-p:my-4"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(getContent("approach_text", `<p>${t("about.approachPara1")}</p><p>${t("about.approachPara2")}</p><p>${t("about.approachPara3")}</p>`)) }}
                  />
                </ScrollReveal>
                <ScrollReveal animation="slideRight">
                  <div className="bg-muted aspect-square flex items-center justify-center">
                    <div className="text-center p-8">
                      <span className="text-6xl md:text-7xl font-display font-medium text-primary">29+</span>
                      <p className="text-primary/70 mt-2">{t("about.yearsExperience")}</p>
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </section>
        )}

        {/* Agency Director Section */}
        {!isSectionHidden("director_title") && (
          <section className="section-padding">
            <div className="container-wide">
              <div className="grid md:grid-cols-2 gap-16 items-center">
                <ScrollReveal animation="slideLeft">
                  <div className="aspect-[4/5] overflow-hidden bg-muted">
                    {shelleyData?.image_url && (
                      <img 
                        src={shelleyData.image_url} 
                        alt="Shelley Cohen"
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                </ScrollReveal>
                <ScrollReveal animation="slideRight">
                  <h2 className="heading-section mb-2 text-primary">
                    {stripHtml(getContent("director_title", t("about.founderTitle")))}
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    {stripHtml(getContent("director_role", t("about.founderInitials")))}
                  </p>
                  <div 
                    className="text-editorial space-y-4 prose prose-p:my-4"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(getContent("director_bio", `<p>${t("about.founderBio1")}</p><p>${t("about.founderBio2")}</p><p>${t("about.founderBio3")}</p>`)) }}
                  />
                </ScrollReveal>
              </div>
            </div>
          </section>
        )}

        {/* Why Producers Choose Us */}
        {!isSectionHidden("whyproducers_title") && (
          <section className="section-padding bg-primary text-primary-foreground">
            <div className="container-wide">
              <ScrollReveal>
                <h2 className="heading-section mb-12">
                  {stripHtml(getContent("whyproducers_title", t("about.whyProducers")))}
                </h2>
              </ScrollReveal>
              <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" staggerDelay={0.1}>
                {whyChooseUs.map((item) => (
                  <div key={item.title} className="border-t border-primary-foreground/20 pt-6">
                    <h3 className="font-display text-xl mb-2">{stripHtml(item.title)}</h3>
                    <p className="text-primary-foreground/70">{stripHtml(item.desc)}</p>
                  </div>
                ))}
              </StaggerContainer>
            </div>
          </section>
        )}

        {/* CTA for prospective talent */}
        <TalentCTA />
      </main>
      <Footer />
    </div>
  );
};

export default About;
